import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import clsx from "clsx"
import axios from "axios"

import classes from "./ProfilePictureUpload.module.scss"
import FormBuilder, {
  FieldsGroup,
} from "../../components/FormBuilder/FormBuilder"
import { BASE_URL } from "../../utils/requests"
import { useSettings } from "../../contexts/SettingsContext"
import { track } from "../../utils/track"
import Button from "../../components/Button/Button"

const fields: FieldsGroup[] = [
  {
    id: "profile",
    label: "העלאת תמונת פרופיל",
    fields: [
      {
        id: "profilePicture",
        name: "profilePicture",
        label: "בחרו תמונת פרופיל",
        type: "file",
        accepts: "image/*",
        required: true,
      },
    ],
  },
]

const ProfilePictureUpload = () => {
  const { settings, setSettings, reloadSettings } = useSettings()
  const navigate = useNavigate()
  useSearchParams()

  const studentUID = settings?.student?.uid

  const [values, setValues] = useState<object>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [, setDone] = useState<boolean>(false)
  const [snackbar, setSnackbar] = useState<string>("")
  const [hideSnackbar, setHideSnackbar] = useState<boolean>(false)
  const [currentImageUrl, setCurrentImageUrl] = useState<string>("")
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (
      settings?.student?.uid &&
      studentUID &&
      !Number.isNaN(studentUID) &&
      settings.student.uid === studentUID &&
      settings.student.profilePicture
    ) {
      setCurrentImageUrl(settings.student.profilePicture)
    }
  }, [settings?.student?.uid, settings?.student?.profilePicture, studentUID])

  // Update local avatar preview immediately when a new file is picked
  useEffect(() => {
    const file = (values as any)["profilePictureFile"] as File | undefined
    if (file instanceof File) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
      const newUrl = URL.createObjectURL(file)
      objectUrlRef.current = newUrl
      setCurrentImageUrl(newUrl)
    }
    // Do not revoke here for current preview; will be revoked on next update
  }, [values])

  // Revoke object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  // Set page title
  useEffect(() => {
    const previousTitle = document.title
    document.title = "העלאת תמונת פרופיל"
    return () => {
      document.title = previousTitle
    }
  }, [])

  const triggerProfileFilePicker = () => {
    const input = document.getElementById(
      "profilePicture",
    ) as HTMLInputElement | null
    input?.click()
  }

  const closeSnackbar = (withAnimation: boolean = false) => {
    if (withAnimation) {
      setHideSnackbar(true)
      setTimeout(() => setSnackbar(""), 320)
      setTimeout(() => setHideSnackbar(false), 322)
      return
    }
    setSnackbar("")
  }

  const showSuccess = (message: string) => {
    setSnackbar(message)
    const closeTimeout = 3000
    setTimeout(() => setHideSnackbar(true), closeTimeout)
    setTimeout(() => closeSnackbar(false), closeTimeout + 320)
    setTimeout(() => setHideSnackbar(false), closeTimeout + 322)
  }

  const onSubmit = async (formValues: object) => {
    setErrors((prev) => ({ ...prev, general: undefined }))

    if (!formValues["profilePictureFile"]) {
      setErrors((prev) => ({ ...prev, profilePicture: "שדה חובה" }))
      return
    }
    if (!studentUID || Number.isNaN(studentUID)) {
      setErrors((prev) => ({ ...prev, general: "מזהה תלמיד לא תקין" }))
      return
    }

    const formData = new FormData()
    formData.append("file", formValues["profilePictureFile"]) // provided by FormBuilder via onFileChange
    // Ensure we don't accidentally navigate due to form submission default
    // The Button triggers onClick, but guard just in case

    try {
      setLoading(true)
      track("Profile Picture Upload Start")
      // 1) Upload the file to storage
      const uploadRes = await axios.post(
        `${BASE_URL}/students/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-Student-UID": studentUID,
            "X-File": "profilePicture",
          },
        },
      )

      const uploadedUrl: string =
        // prefer explicit shape from backend example
        (uploadRes?.data && (uploadRes.data.url as string)) || ""

      if (!uploadedUrl) {
        track("Profile Picture Upload Failed")
        throw new Error("Missing uploaded url")
      }

      // Update local avatar preview immediately (already handled by values effect, but safe here too)
      if (formValues["profilePictureFile"]) {
        const objectUrl = URL.createObjectURL(
          formValues["profilePictureFile"],
        ) as string
        setCurrentImageUrl(objectUrl)
      }

      // 2) Persist the new profile picture URL on the student via PATCH
      const token = settings?.token
      if (!token) {
        throw new Error("Missing auth token")
      }

      await axios.patch(
        `${BASE_URL}/student/edit`,
        { profilePicture: uploadedUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      track("Profile Picture Upload Success", { picture: uploadedUrl })

      // Update settings locally so avatar is in sync everywhere
      if (settings?.student) {
        setSettings({
          student: { ...settings.student, profilePicture: uploadedUrl } as any,
        })
      }

      setDone(true)
      // Add done=true param to URL without losing current params
      const newParams = new URLSearchParams()
      newParams.set("done", "true")
      navigate({ search: newParams.toString() }, { replace: true })
      showSuccess("התמונה נשמרה בהצלחה")
    } catch (err) {
      setErrors((prev) => ({ ...prev, general: "שגיאה בשמירת התמונה" }))
    } finally {
      setLoading(false)
    }
  }

  const onResetProfilePicture = async () => {
    if (window.confirm("האם אתה בטוח שברצונך למחוק את תמונת הפרופיל?")) {
      const token = settings?.token

      if (!token) {
        throw new Error("Missing auth token")
      }

      await axios.patch(`${BASE_URL}/student/profile/reset`, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      track("Profile Picture Reset Success")

      await reloadSettings()

      setDone(true)
      // Add done=true param to URL without losing current params
      const newParams = new URLSearchParams()
      newParams.set("done", "true")
      navigate({ search: newParams.toString() }, { replace: true })
    }
  }

  return (
    <>
      <div className={classes.root}>
        <div className={clsx(classes.header, "padded")}>
          <div className={classes.avatarWrapper}>
            <div
              className={classes.avatar}
              role="button"
              tabIndex={0}
              aria-label="בחרו תמונת פרופיל חדשה"
              onClick={triggerProfileFilePicker}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  triggerProfileFilePicker()
                }
              }}
            >
              <img src={currentImageUrl ?? "/avatar.png"} alt="avatar" />
            </div>
          </div>
        </div>

        <div className={clsx(classes.content, classes.form, "padded")}>
          <FormBuilder
            groups={fields}
            values={values}
            onChange={setValues}
            onSubmit={onSubmit}
            withCaptcha={false}
            errors={errors}
            onErrorsChange={setErrors}
            buttonLabel={loading ? "שומר..." : "שמירה"}
          />
          <Button
            color="error"
            variant="outlined"
            className={classes.resetBtn}
            onClick={onResetProfilePicture}
          >
            איפוס תמונת הפרופיל
          </Button>
        </div>
      </div>

      {!!snackbar && (
        <div
          className={clsx(classes.snackbar, {
            [classes.active]: !!snackbar,
            [classes.hide]: hideSnackbar,
          })}
        >
          <span>{snackbar}</span>
          <button
            className={classes.action}
            onClick={() => closeSnackbar(true)}
          >
            סגירה
          </button>
        </div>
      )}
    </>
  )
}

export default ProfilePictureUpload
